import React, { useEffect } from "react";
import { Box, Typography, styled } from "@mui/material";
import { useNavigate, Link } from "react-router-dom";

const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4feff",
  minHeight: "100vh",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "40px 20px",
  [theme.breakpoints.down("md")]: {
    padding: "32px 16px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 8px",
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  maxWidth: "800px",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "0px",
  boxSizing: "border-box",
  padding: "32px",
  backgroundColor: "#ffffff",
  borderRadius: "16px",
  border: "1px solid #05495a",
  boxShadow: "0px 4px 24px 0px rgba(10,130,157,0.1)",
  [theme.breakpoints.down("md")]: {
    gap: "0px",
    padding: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    gap: "0px",
    padding: "12px",
    borderRadius: "12px",
    maxWidth: "95%",
  },
}));

const BackButton = styled("button")(({ theme }) => ({
  alignSelf: "flex-start",
  background: "none",
  border: "none",
  color: "#0a829d",
  fontSize: "16px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 0",
  fontFamily: '"Rethink Sans", sans-serif',
  "&:hover": {
    textDecoration: "underline",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    padding: "6px 0",
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: "28px",
  lineHeight: "35px",
  color: "#033843",
  textAlign: "center",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    fontSize: "24px",
    lineHeight: "30px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
    lineHeight: "26px",
    marginBottom: "6px",
  },
}));

const LastUpdated = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  color: "#737373",
  textAlign: "center",
  marginBottom: "16px",
  [theme.breakpoints.down("md")]: {
    fontSize: "13px",
    marginBottom: "12px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    marginBottom: "10px",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "24px",
  color: "#033843",
  marginBottom: "8px",
  marginTop: "16px",
  [theme.breakpoints.down("md")]: {
    fontSize: "16px",
    lineHeight: "22px",
    marginTop: "14px",
    marginBottom: "6px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "15px",
    lineHeight: "20px",
    marginTop: "12px",
    marginBottom: "6px",
  },
}));

const SectionContent = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#262626",
  marginBottom: "8px",
  wordBreak: "normal",
  overflowWrap: "break-word",
  hyphens: "none",
  whiteSpace: "normal",
  [theme.breakpoints.down("md")]: {
    fontSize: "13px",
    lineHeight: "18px",
    marginBottom: "6px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    lineHeight: "16px",
    marginBottom: "6px",
  },
}));

const BulletList = styled(Box)(({ theme }) => ({
  paddingLeft: "20px",
  marginBottom: "8px",
  "& li": {
    fontFamily: "Rethink Sans, sans-serif",
    fontSize: "14px",
    lineHeight: "20px",
    color: "#262626",
    marginBottom: "6px",
  },
  [theme.breakpoints.down("md")]: {
    paddingLeft: "18px",
    marginBottom: "6px",
    "& li": {
      fontSize: "13px",
      lineHeight: "18px",
      marginBottom: "4px",
    },
  },
  [theme.breakpoints.down("sm")]: {
    paddingLeft: "16px",
    marginBottom: "6px",
    "& li": {
      fontSize: "12px",
      lineHeight: "16px",
      marginBottom: "4px",
    },
  },
}));

const ContactInfo = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4feff",
  border: "1px solid #bfe5ec",
  borderRadius: "8px",
  padding: "20px",
  marginTop: "16px",
  "& p": {
    fontFamily: "Rethink Sans, sans-serif",
    fontSize: "14px",
    lineHeight: "20px",
    color: "#262626",
    margin: "3px 0",
  },
  [theme.breakpoints.down("md")]: {
    padding: "16px",
    marginTop: "14px",
    "& p": {
      fontSize: "13px",
      lineHeight: "18px",
      margin: "2px 0",
    },
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px",
    marginTop: "12px",
    borderRadius: "6px",
    "& p": {
      fontSize: "12px",
      lineHeight: "16px",
      margin: "2px 0",
    },
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: "#0a829d",
  textDecoration: "none",
  fontFamily: "Rethink Sans, sans-serif",
  "&:hover": {
    textDecoration: "underline",
  },
}));

export default function TermsOfService() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <PageContainer>
      <ContentContainer>
        <BackButton onClick={handleBack}>← Back</BackButton>

        <Title>UniComply.ai Terms of Service</Title>
        <LastUpdated>Last updated: September 07, 2025</LastUpdated>

        <SectionContent>
          Welcome to UniComply.ai ("Company", "we", "our", or "us"). These Terms
          of Service ("Terms") govern your access to and use of the UniComply.ai
          compliance hub, including our website (www.unicomply.ai), features
          such as Compliance Explorer, regulatory setup, learning and training
          modules, penalties tracker, policy templates, and all related tools
          and services (collectively, the "Services").
        </SectionContent>

        <SectionContent>
          By accessing or using our Services, you agree to be bound by these
          Terms. If you do not agree, please discontinue use of the Services.
        </SectionContent>

        <SectionTitle>Eligibility and Accounts</SectionTitle>
        <SectionContent>
          You must be at least 18 years old to use UniComply.ai Services. By
          creating an account, you agree that the information you provide is
          accurate and kept up to date.
        </SectionContent>
        <BulletList>
          <li>
            If you are using the Services on behalf of an organization, you
            represent that you have the authority to bind that entity to these
            Terms.
          </li>
          <li>
            You are responsible for maintaining the confidentiality of your
            account credentials and all activities under your account.
          </li>
        </BulletList>

        <SectionTitle>Your Content and Responsibilities</SectionTitle>
        <SectionContent>
          You may upload, submit, or input information (collectively, "Your
          Content") into the Services. You retain ownership of Your Content, but
          grant UniComply.ai a worldwide, non-exclusive license to use, store,
          and process Your Content solely to provide the Services. You are
          responsible for ensuring Your Content complies with applicable laws
          and does not infringe the rights of third parties. UniComply.ai may
          remove content that violates these Terms.
        </SectionContent>

        <SectionTitle>Paid Subscriptions and Billing</SectionTitle>
        <SectionContent>
          Some Services are offered free of charge, while others require a paid
          subscription. By subscribing, you agree to pay all applicable fees and
          taxes.
        </SectionContent>
        <BulletList>
          <li>
            Fees are billed in advance and are non-refundable unless required by
            law. Subscriptions automatically renew unless cancelled prior to the
            renewal date.
          </li>
          <li>
            You authorize us to charge your payment method for recurring fees.
            UniComply.ai reserves the right to change subscription pricing with
            reasonable notice.
          </li>
        </BulletList>

        <SectionTitle>Prohibited Uses</SectionTitle>
        <SectionContent>
          You agree not to misuse the Services. Prohibited uses include
          attempting to reverse engineering platforms; uploading malware;
          engaging in fraudulent, defamatory, or abusive activities; violating
          intellectual property rights; sending spam; or using the Services in
          violation of applicable law.
        </SectionContent>
        <SectionContent>
          In addition to pursuing other actions permitted by law, UniComply.ai
          may suspend or terminate accounts engaging in prohibited conduct.
        </SectionContent>

        <SectionTitle>Third-Party Services and Links</SectionTitle>
        <SectionContent>
          The Services may contain links to third-party websites or allow
          integrations with third-party tools. UniComply.ai is not responsible
          for the content, accuracy, or practices of third-party services. Your
          use of such services is at your own risk and subject to the terms of
          those third parties.
        </SectionContent>

        <SectionTitle>
          HIPAA Compliance and Business Associate Agreement (BAA)
        </SectionTitle>
        <SectionContent>
          If you are a "Covered Entity" or "Business Associate" as defined under
          the Health Insurance Portability and Accountability Act of 1996
          ("HIPAA") and the Health Information Technology for Economic and
          Clinical Health Act ("HITECH Act"), and you include Protected Health
          Information ("PHI") in data processed through UniComply.ai, the
          AffableONE HIPAA Business Associate Agreement (BAA) automatically
          applies.
        </SectionContent>
        <SectionContent>
          The BAA is incorporated by reference into these Terms and governs our
          handling of PHI as a Business Associate under HIPAA. It sets out how
          we use, disclose, and safeguard PHI, our breach notification process,
          and subcontractor compliance (including Microsoft Azure hosting under
          its own HIPAA BAA).
        </SectionContent>
        <SectionContent>
          By using UniComply.ai to store, transmit, or process PHI, you
          acknowledge and agree to the terms of the BAA. The BAA is available
          at:{" "}
          <StyledLink to="/hipaa-baa">
            https://unicomply.ai/hipaa-baa
          </StyledLink>
        </SectionContent>

        <SectionTitle>Intellectual Property Rights</SectionTitle>
        <SectionContent>
          UniComply.ai retains all rights, title, and interest in and to the
          Services, including all software, design, and content, except for Your
          Content.
        </SectionContent>
        <BulletList>
          <li>
            You may not copy, modify, or distribute any part of the Services
            without prior written consent of UniComply.ai. Feedback you provide
            may be used by UniComply.ai without any obligation to compensate
            you.
          </li>
        </BulletList>

        <SectionTitle>Termination</SectionTitle>
        <SectionContent>
          You may terminate your account at any time by contacting
          support@affableone.ai. We may suspend or terminate your account if you
          violate these Terms or misuse the Services.
        </SectionContent>
        <BulletList>
          <li>
            Upon termination, your access to the Services will cease, and
            certain obligations, such as payment and indemnification, will
            survive termination.
          </li>
        </BulletList>

        <SectionTitle>Disclaimers</SectionTitle>
        <SectionContent>
          The Services are provided "as is" and "as available" without
          warranties of any kind, whether express or implied. UniComply.ai
          disclaims warranties of merchantability, fitness for a particular
          purpose, and non-infringement. We do not warrant that the Services
          will be error-free, secure, or uninterrupted.
        </SectionContent>

        <SectionTitle>Limitations of Liability</SectionTitle>
        <SectionContent>
          To the maximum extent permitted by law, UniComply.ai will not be
          liable for indirect, incidental, special, or consequential damages
          arising from your use of the Services. Our total liability will not
          exceed the greater of (a) $100 or (b) the fees you paid us in the 12
          months preceding the claim.
        </SectionContent>

        <SectionTitle>Indemnification</SectionTitle>
        <SectionContent>
          You agree to indemnify and hold UniComply.ai and its affiliates
          harmless from claims, damages, liabilities, and expenses arising from
          your use of the Services, Your Content, or your violation of these
          Terms.
        </SectionContent>

        <SectionTitle>Governing Law and Dispute Resolution</SectionTitle>
        <SectionContent>
          These Terms are governed by the laws of the State of Washington,
          United States. Any disputes will be resolved through binding
          arbitration on an individual basis in King County, Washington. You
          waive the right to participate in class actions or jury trials. You
          may opt out of arbitration within 30 days of accepting these Terms by
          contacting support@affableone.ai.
        </SectionContent>

        <SectionTitle>Changes to the Services and Terms</SectionTitle>
        <SectionContent>
          We may modify or discontinue Services, and we may update these Terms.
          Changes will be posted with a new "Last Updated" date. Continued use
          of the Services after updates indicates your acceptance of the revised
          Terms.
        </SectionContent>

        <SectionTitle>Contact Us</SectionTitle>
        <ContactInfo>
          <Typography component="p">
            If you have any questions or concerns about these Terms, please
            contact us at:
          </Typography>
          <Typography component="p">
            UniComply.ai, Sammamish, WA 98101, United States
          </Typography>
          <Typography component="p">Email: support@affableone.ai</Typography>
        </ContactInfo>
      </ContentContainer>
    </PageContainer>
  );
}
